// CT 08 · Data Tables, Fields & Relationships — Table Detective practice bank.
//
// Converted from the inert markdown brief (table-detective-exercise.md, whose
// Parts 1-5 were blank tables the learner filled in by hand and nobody graded)
// into the same gradable shape the Statistics Clinic DCC cards use: scenario
// items + per-lens answerGuide/ruleTerms/criticalTerms driving
// TakeHomePracticeBank's semantic-rubric-grade + rules fallback.
//
// Every table below is real data copied from
// /data/datasets/table-detective-retail-export.csv, and every expected answer
// agrees with /data/datasets/table-detective-answer-key.csv, so the learner can
// verify the maths against the export they downloaded.

export const CT08_TABLE_DETECTIVE_TRACKER_ID = 'ct-08-table-detective-practice-bank';

export const CT08_TABLE_DETECTIVE_LENSES = [
  ['business_question', 'Business question'],
  ['grain', 'Grain (what one row means)'],
  ['row_identifier', 'Row identifier'],
  ['field_roles', 'Field roles'],
  ['tables_proposed', 'Tables proposed'],
  ['keys', 'Primary & foreign keys'],
  ['relationship', 'Relationship & cardinality'],
  ['risk_avoided', 'Risk avoided'],
  ['caution', 'Caution'],
];

export const CT08_TABLE_DETECTIVE_ITEMS = [
  {
    id: 'TD01',
    title: 'TD 01: The order count that looks wrong',
    scenario: 'The Northstar sales lead reports "15 orders this period" straight from the row count of the flat export. Finance says the till system only raised 10 order numbers. Nobody has changed the data — the disagreement is about what a single row of this export actually is.',
    tables: [{
      columns: ['order_id', 'order_line_id', 'order_date', 'product_id', 'line_total'],
      rows: [
        ['O-1001', 'O-1001-1', '2026-06-28', 'P-100', '36.00'],
        ['O-1001', 'O-1001-2', '2026-06-28', 'P-214', '37.80'],
        ['O-1002', 'O-1002-1', '2026-06-28', 'P-330', '102.00'],
        ['O-1003', 'O-1003-1', '2026-06-29', 'P-100', '18.00'],
        ['O-1003', 'O-1003-2', '2026-06-29', 'P-410', '247.00'],
      ],
    }],
    answerGuide: {
      business_question: 'How many orders did Northstar actually take, and what does one row of this export represent?',
      grain: 'One row per order line — one product inside one order — not one row per order. O-1001 and O-1003 each take two rows.',
      row_identifier: 'order_line_id is unique on every row. order_id repeats, so it cannot identify a row on its own.',
      field_roles: 'order_id and order_line_id are identifiers, order_date is date/time, product_id is an identifier, line_total is a measure.',
      tables_proposed: 'Orders (one row per order) and OrderItems (one row per order line). The order count has to be taken at the Orders grain.',
      keys: 'Orders: primary key order_id. OrderItems: primary key order_line_id, foreign key order_id back to Orders.',
      relationship: 'Orders to OrderItems is one-to-many — one order can contain many product lines.',
      risk_avoided: 'Reporting 15 orders instead of 10 — an inflated order count caused by counting order lines as orders.',
      caution: 'Revenue can still be summed at line level, but any count of orders must use distinct order_id, not row count.',
    },
    ruleTerms: {
      business_question: ['how many orders', 'one row', 'represent'],
      grain: ['order line', 'per order line', 'not one row per order'],
      row_identifier: ['order_line_id', 'unique'],
      field_roles: ['identifier', 'measure', 'date'],
      tables_proposed: ['orders', 'orderitems'],
      keys: ['order_id', 'order_line_id', 'foreign key'],
      relationship: ['one-to-many', 'many product lines'],
      risk_avoided: ['15', '10', 'inflated'],
      caution: ['distinct', 'order_id', 'row count'],
    },
    criticalTerms: { grain: ['order line'], row_identifier: ['order_line_id'] },
  },
  {
    id: 'TD02',
    title: 'TD 02: The customer who moved',
    scenario: 'Amara Okafor has moved from the North region to the South and rang to update her record. In the flat export her region sits on every single line she appears on, and the ops assistant has been told to "just change it". She wants to know where that fact should live so one edit is enough.',
    tables: [{
      columns: ['order_id', 'order_line_id', 'customer_id', 'customer_name', 'customer_region', 'customer_segment'],
      rows: [
        ['O-1001', 'O-1001-1', 'C-042', 'Amara Okafor', 'North', 'Consumer'],
        ['O-1001', 'O-1001-2', 'C-042', 'Amara Okafor', 'North', 'Consumer'],
        ['O-1004', 'O-1004-1', 'C-042', 'Amara Okafor', 'North', 'Consumer'],
      ],
    }],
    answerGuide: {
      business_question: 'Where should customer region live so it can be corrected once instead of on every order line?',
      grain: 'The export is one row per order line, so C-042 customer facts repeat on all 3 of her rows.',
      row_identifier: 'customer_id (C-042) identifies the customer; order_line_id still identifies the row.',
      field_roles: 'customer_id is an identifier. customer_name, customer_region and customer_segment are dimensions — they describe the customer, not the sale.',
      tables_proposed: 'Customers — one row per customer holding customer_id, customer_name, customer_region and customer_segment.',
      keys: 'Customers: primary key customer_id. Orders carries customer_id as a foreign key.',
      relationship: 'Customers to Orders is one-to-many — C-042 already has two orders, O-1001 and O-1004.',
      risk_avoided: 'An update anomaly: editing the region on some rows and not the others leaves the same customer in two regions at once.',
      caution: 'If the business means "region at the time of the sale" rather than "where the customer lives now", that fact belongs on the order, not the customer — confirm the meaning before moving it.',
    },
    ruleTerms: {
      business_question: ['region', 'once', 'customer'],
      grain: ['order line', 'repeat'],
      row_identifier: ['customer_id', 'c-042'],
      field_roles: ['dimension', 'describe the customer', 'identifier'],
      tables_proposed: ['customers', 'one row per customer'],
      keys: ['customer_id', 'primary key', 'foreign key'],
      relationship: ['one-to-many', 'o-1001', 'o-1004'],
      risk_avoided: ['update anomaly', 'two regions', 'inconsistent'],
      caution: ['at the time', 'historical', 'confirm'],
    },
    criticalTerms: { row_identifier: ['customer_id'], tables_proposed: ['Customers'] },
  },
  {
    id: 'TD03',
    title: 'TD 03: The category that appears twice in the report',
    scenario: 'Merchandising runs a sales-by-category report and "Accessories" shows up twice, because some rows in the source system were typed as "Acessories". Fixing it in the flat export means finding and editing every affected line, and the same typo can reappear tomorrow.',
    tables: [{
      columns: ['order_line_id', 'product_id', 'product_name', 'product_category', 'unit_price'],
      rows: [
        ['O-1001-1', 'P-100', 'Wireless Mouse', 'Accessories', '18.00'],
        ['O-1001-2', 'P-214', 'USB-C Hub', 'Accessories', '42.00'],
        ['O-1003-1', 'P-100', 'Wireless Mouse', 'Accessories', '18.00'],
        ['O-1005-2', 'P-700', 'Laptop Stand', 'Accessories', '35.00'],
        ['O-1007-1', 'P-100', 'Wireless Mouse', 'Accessories', '18.00'],
      ],
    }],
    answerGuide: {
      business_question: 'Where should product category live so a spelling fix happens once and cannot split the report again?',
      grain: 'One row per order line, so P-100 product facts are retyped on every line that sold a Wireless Mouse — 3 of them here.',
      row_identifier: 'product_id (P-100) identifies the product; order_line_id identifies the row.',
      field_roles: 'product_id is an identifier. product_name and product_category are dimensions describing the product. unit_price is a measure held at line level.',
      tables_proposed: 'Products — one row per product holding product_id, product_name and product_category.',
      keys: 'Products: primary key product_id. OrderItems carries product_id as a foreign key.',
      relationship: 'Products to OrderItems is one-to-many — one product can appear in many order lines. P-100 appears in O-1001, O-1003 and O-1007.',
      risk_avoided: 'Inconsistent category spelling splitting one category into two rows of a report, and having to edit every sales line to fix it.',
      caution: 'Moving the category out is only half the fix — the Products table also needs a controlled list or constraint, or the same typo can be entered again at product level.',
    },
    ruleTerms: {
      business_question: ['category', 'once', 'fix'],
      grain: ['order line', 'retyped', 'repeat'],
      row_identifier: ['product_id', 'p-100'],
      field_roles: ['dimension', 'identifier', 'measure'],
      tables_proposed: ['products', 'one row per product'],
      keys: ['product_id', 'primary key', 'foreign key'],
      relationship: ['one-to-many', 'many order lines'],
      risk_avoided: ['spelling', 'split', 'inconsistent'],
      caution: ['constraint', 'controlled list', 'again'],
    },
    criticalTerms: { row_identifier: ['product_id'], tables_proposed: ['Products'] },
  },
  {
    id: 'TD04',
    title: 'TD 04: The branch that is being renamed',
    scenario: 'Store S-03 is being rebranded from "Bristol West" to "Bristol Central" next month. The finance team is nervous: in the flat export the store name is written on every order line that store ever handled, and they do not want to touch historic sales rows to make a rename happen.',
    tables: [{
      columns: ['order_id', 'order_line_id', 'store_id', 'store_name', 'store_region'],
      rows: [
        ['O-1002', 'O-1002-1', 'S-03', 'Bristol West', 'South'],
        ['O-1006', 'O-1006-1', 'S-03', 'Bristol West', 'South'],
        ['O-1010', 'O-1010-1', 'S-03', 'Bristol West', 'South'],
        ['O-1010', 'O-1010-2', 'S-03', 'Bristol West', 'South'],
      ],
    }],
    answerGuide: {
      business_question: 'Where should store name live so a rename is one edit and no sales rows have to be touched?',
      grain: 'One row per order line, so S-03 store details repeat on every line that store handled — 4 rows here across 3 orders.',
      row_identifier: 'store_id (S-03) identifies the store; order_line_id identifies the row.',
      field_roles: 'store_id is an identifier. store_name and store_region are dimensions that describe the store, not the sale.',
      tables_proposed: 'Stores — one row per store holding store_id, store_name and store_region.',
      keys: 'Stores: primary key store_id. Orders carries store_id as a foreign key.',
      relationship: 'Stores to Orders is one-to-many — one store handles many orders. S-03 already has O-1002, O-1006 and O-1010.',
      risk_avoided: 'A rename turning into a mass edit of historic sales rows, with the risk of a partial update leaving both names live at once.',
      caution: 'If a report must show the store name as it was at the time of the sale, a single current name is not enough — that needs a history/effective-date design, which is beyond this model.',
    },
    ruleTerms: {
      business_question: ['store name', 'rename', 'one edit'],
      grain: ['order line', 'repeat'],
      row_identifier: ['store_id', 's-03'],
      field_roles: ['dimension', 'identifier', 'describe the store'],
      tables_proposed: ['stores', 'one row per store'],
      keys: ['store_id', 'primary key', 'foreign key'],
      relationship: ['one-to-many', 'many orders'],
      risk_avoided: ['mass edit', 'partial update', 'both names'],
      caution: ['at the time', 'history', 'effective date'],
    },
    criticalTerms: { row_identifier: ['store_id'], tables_proposed: ['Stores'] },
  },
  {
    id: 'TD05',
    title: 'TD 05: Two facts that do not sit at the same level',
    scenario: 'A junior analyst is drafting the model and wants to put payment_method and discount_pct in the same table "because they are both about the sale". Look at order O-1001: both lines are paid by Card, but only the second line was discounted. O-1005 is the same story with a different pattern.',
    tables: [{
      columns: ['order_id', 'order_line_id', 'product_id', 'discount_pct', 'payment_method'],
      rows: [
        ['O-1001', 'O-1001-1', 'P-100', '0.00', 'Card'],
        ['O-1001', 'O-1001-2', 'P-214', '0.10', 'Card'],
        ['O-1005', 'O-1005-1', 'P-214', '0.05', 'Invoice'],
        ['O-1005', 'O-1005-2', 'P-700', '0.05', 'Invoice'],
      ],
    }],
    answerGuide: {
      business_question: 'Which of these two fields belongs on the order and which belongs on the order line?',
      grain: 'One row per order line. payment_method holds the same value across every line of an order; discount_pct changes between lines of the same order.',
      row_identifier: 'order_id identifies the order-level fact; order_line_id identifies the line-level fact.',
      field_roles: 'payment_method is an order-level dimension. discount_pct is a line-level measure (a rate), and it is not additive.',
      tables_proposed: 'payment_method goes in Orders. discount_pct goes in OrderItems alongside quantity and unit_price.',
      keys: 'Orders: primary key order_id, holding order_date, customer_id, store_id and payment_method. OrderItems: primary key order_line_id, foreign key order_id.',
      relationship: 'Orders to OrderItems is one-to-many, which is exactly why an order-level fact stated once can serve many lines.',
      risk_avoided: 'Putting discount_pct on Orders would lose the 0.10 discount on O-1001-2, because O-1001 would only be allowed one discount value.',
      caution: 'The test is not "does it feel like a sale fact" but "does it change within an order". Confirm with a bigger export that payment_method never varies within one order before fixing it at order level.',
    },
    ruleTerms: {
      business_question: ['order', 'order line', 'belongs'],
      grain: ['same value', 'changes', 'order line'],
      row_identifier: ['order_id', 'order_line_id'],
      field_roles: ['dimension', 'measure', 'rate', 'not additive'],
      tables_proposed: ['orders', 'orderitems', 'payment_method', 'discount_pct'],
      keys: ['order_id', 'order_line_id', 'foreign key'],
      relationship: ['one-to-many'],
      risk_avoided: ['0.10', 'lose', 'o-1001-2'],
      caution: ['changes within', 'vary', 'confirm'],
    },
    criticalTerms: { tables_proposed: ['payment_method'], risk_avoided: ['0.10'] },
  },
  {
    id: 'TD06',
    title: 'TD 06: The join that will not work',
    scenario: 'The buying team wants a report linking orders to products and asks "can we not just join Orders straight to Products?" In this sample, order O-1001 contains two different products, and product P-100 turns up in three different orders. Decide whether a direct link can hold.',
    tables: [{
      columns: ['order_id', 'order_line_id', 'product_id', 'product_name', 'quantity'],
      rows: [
        ['O-1001', 'O-1001-1', 'P-100', 'Wireless Mouse', '2'],
        ['O-1001', 'O-1001-2', 'P-214', 'USB-C Hub', '1'],
        ['O-1003', 'O-1003-1', 'P-100', 'Wireless Mouse', '1'],
        ['O-1007', 'O-1007-1', 'P-100', 'Wireless Mouse', '3'],
        ['O-1007', 'O-1007-2', 'P-330', 'Office Chair', '1'],
      ],
    }],
    answerGuide: {
      business_question: 'Can Orders link directly to Products, or is something needed in between?',
      grain: 'One row per order line — the line is the only place where one order meets one product.',
      row_identifier: 'order_line_id. Neither order_id nor product_id is unique in this sample.',
      field_roles: 'order_id and product_id are identifiers (foreign keys on the line), product_name is a dimension, quantity is a measure.',
      tables_proposed: 'Orders, Products, and OrderItems as the bridge/detail table between them.',
      keys: 'OrderItems: primary key order_line_id, foreign keys order_id and product_id. The pair (order_id, product_id) is what actually links the two sides.',
      relationship: 'Orders to Products is many-to-many, resolved through OrderItems. Orders to OrderItems is one-to-many and Products to OrderItems is one-to-many.',
      risk_avoided: 'A direct Orders-to-Products link would force one product per order, or duplicate order rows per product and double count order-level values.',
      caution: 'Once the bridge exists, order-level totals must be aggregated from the lines, not read off a joined row, or the same order value is counted once per product.',
    },
    ruleTerms: {
      business_question: ['directly', 'between', 'link'],
      grain: ['order line', 'one order', 'one product'],
      row_identifier: ['order_line_id', 'not unique'],
      field_roles: ['identifier', 'foreign key', 'measure', 'dimension'],
      tables_proposed: ['orderitems', 'bridge', 'orders', 'products'],
      keys: ['order_id', 'product_id', 'foreign key'],
      relationship: ['many-to-many', 'resolved', 'one-to-many'],
      risk_avoided: ['one product per order', 'duplicate', 'double count'],
      caution: ['aggregate', 'double', 'lines'],
    },
    criticalTerms: { relationship: ['many-to-many'], tables_proposed: ['OrderItems'] },
  },
  {
    id: 'TD07',
    title: 'TD 07: Can we still calculate sales after the split?',
    scenario: 'The finance manager has been shown the five-table model and is unconvinced: "you have taken my one flat file and cut it into pieces — can you still tell me what order O-1005 was worth?" Prove that no revenue was lost by splitting, using the numbers in front of you.',
    tables: [{
      columns: ['order_id', 'order_line_id', 'product_id', 'quantity', 'unit_price', 'discount_pct', 'line_total'],
      rows: [
        ['O-1005', 'O-1005-1', 'P-214', '4', '42.00', '0.05', '159.60'],
        ['O-1005', 'O-1005-2', 'P-700', '4', '35.00', '0.05', '133.00'],
        ['O-1001', 'O-1001-1', 'P-100', '2', '18.00', '0.00', '36.00'],
        ['O-1001', 'O-1001-2', 'P-214', '1', '42.00', '0.10', '37.80'],
      ],
    }],
    answerGuide: {
      business_question: 'What is order O-1005 worth, and can order value still be produced once the flat export is split into tables?',
      grain: 'One row per order line. Order value is not stored anywhere — it is an aggregate of the lines.',
      row_identifier: 'order_line_id for each line; order_id is the level the total is grouped to.',
      field_roles: 'quantity and line_total are additive measures. unit_price and discount_pct are non-additive (a price and a rate). line_total is derived: quantity x unit_price x (1 - discount_pct).',
      tables_proposed: 'quantity, unit_price, discount_pct and line_total stay in OrderItems. Orders holds no total — it is calculated as SUM(line_total) grouped by order_id.',
      keys: 'OrderItems: primary key order_line_id, foreign key order_id, which is what makes the grouping possible.',
      relationship: 'Orders to OrderItems is one-to-many, so one order total is the sum of its many lines.',
      risk_avoided: 'Storing an order total on every line and then summing it — O-1005 would be counted twice. Splitting the table removes that trap.',
      caution: 'Check line_total: O-1005-1 = 4 x 42.00 x 0.95 = 159.60 and O-1005-2 = 4 x 35.00 x 0.95 = 133.00, so O-1005 = 292.60. O-1001 = 36.00 + 37.80 = 73.80. A stored derived column can drift from its inputs, so recalculate rather than trust it.',
    },
    ruleTerms: {
      business_question: ['o-1005', 'order value', 'worth'],
      grain: ['order line', 'aggregate'],
      row_identifier: ['order_line_id', 'order_id'],
      field_roles: ['additive', 'measure', 'derived', 'rate'],
      tables_proposed: ['orderitems', 'sum', 'group'],
      keys: ['order_id', 'foreign key'],
      relationship: ['one-to-many', 'sum of its'],
      risk_avoided: ['double count', 'twice', 'stored total'],
      caution: ['159.60', '133.00', '292.60'],
    },
    criticalTerms: { caution: ['292.60'], business_question: ['O-1005'] },
  },
  {
    id: 'TD08',
    title: 'TD 08: Everything numeric is not a measure',
    scenario: 'A junior analyst has dropped every numeric column of order O-1001 into a SUM and produced a summary that includes a total unit_price of 60.00 and a total discount of 0.10. The line manager wants to know which of those numbers are meaningful and which are nonsense.',
    tables: [{
      columns: ['order_id', 'order_line_id', 'quantity', 'unit_price', 'discount_pct', 'line_total'],
      rows: [
        ['O-1001', 'O-1001-1', '2', '18.00', '0.00', '36.00'],
        ['O-1001', 'O-1001-2', '1', '42.00', '0.10', '37.80'],
      ],
    }],
    answerGuide: {
      business_question: 'Which numeric fields on this export can be summed, and which cannot?',
      grain: 'One row per order line, so every number here describes one product line, not the whole order.',
      row_identifier: 'order_line_id. The identifier columns are not measures even when they look sortable.',
      field_roles: 'quantity (3 units) and line_total (73.80) are additive measures. unit_price and discount_pct are non-additive — a price and a rate. order_id and order_line_id are identifiers.',
      tables_proposed: 'All four numeric fields sit in OrderItems, but only quantity and line_total should be exposed to a SUM in a report.',
      keys: 'OrderItems: primary key order_line_id, foreign key order_id. Unit price also belongs on Products as a list price if the business needs one — the line keeps the price actually charged.',
      relationship: 'Orders to OrderItems is one-to-many, so line-level measures roll up to the order by summing, while rates must be recalculated.',
      risk_avoided: 'A summary reporting a total unit_price of 60.00 and a total discount of 0.10, neither of which means anything to the business.',
      caution: 'The correct average discount is weighted, not a plain average: total discount given = 4.20 on a 78.00 gross, not "5%". Recompute rates from their numerator and denominator.',
    },
    ruleTerms: {
      business_question: ['summed', 'cannot', 'numeric'],
      grain: ['order line', 'one product line'],
      row_identifier: ['order_line_id', 'identifier'],
      field_roles: ['additive', 'non-additive', 'rate', '73.80'],
      tables_proposed: ['orderitems', 'quantity', 'line_total'],
      keys: ['order_line_id', 'foreign key', 'list price'],
      relationship: ['one-to-many', 'roll up', 'recalculated'],
      risk_avoided: ['60.00', 'total unit_price', 'nonsense'],
      caution: ['weighted', 'recompute', 'denominator'],
    },
    criticalTerms: { field_roles: ['73.80'], risk_avoided: ['60.00'] },
  },
  {
    id: 'TD09',
    title: 'TD 09: The daily trend that overstates Monday',
    scenario: 'A daily orders chart is built by counting rows per order_date. For 2026-06-28 it shows 3, and the store manager says only 2 order numbers were raised that day. The date is correct on every row — the problem is what is being counted.',
    tables: [{
      columns: ['order_date', 'order_id', 'order_line_id', 'product_id', 'line_total'],
      rows: [
        ['2026-06-28', 'O-1001', 'O-1001-1', 'P-100', '36.00'],
        ['2026-06-28', 'O-1001', 'O-1001-2', 'P-214', '37.80'],
        ['2026-06-28', 'O-1002', 'O-1002-1', 'P-330', '102.00'],
      ],
    }],
    answerGuide: {
      business_question: 'How many orders were taken on 2026-06-28, and which table should the date live in?',
      grain: 'One row per order line, so a day with 3 lines can be a day with only 2 orders.',
      row_identifier: 'order_line_id identifies the row; order_id is what a daily order count must be distinct on.',
      field_roles: 'order_date is a date/time field describing the order — it is not a property of the product or the line.',
      tables_proposed: 'order_date belongs in Orders, one row per order, alongside customer_id, store_id and payment_method.',
      keys: 'Orders: primary key order_id holding order_date. OrderItems carries order_id as a foreign key and inherits the date through the join.',
      relationship: 'Orders to OrderItems is one-to-many, so one date serves many lines and must not be counted once per line.',
      risk_avoided: 'A trend chart that overstates daily order volume — 3 rows read as 3 orders when the day only had 2 (O-1001 and O-1002).',
      caution: 'Daily revenue is still correct at line level (36.00 + 37.80 + 102.00 = 175.80). Only the count is wrong, so fix the count without changing how revenue is summed.',
    },
    ruleTerms: {
      business_question: ['2026-06-28', 'orders', 'date'],
      grain: ['order line', '3 lines', '2 orders'],
      row_identifier: ['order_line_id', 'distinct', 'order_id'],
      field_roles: ['date', 'describes the order', 'time'],
      tables_proposed: ['orders', 'order_date'],
      keys: ['order_id', 'primary key', 'foreign key'],
      relationship: ['one-to-many', 'many lines'],
      risk_avoided: ['overstate', '3', '2'],
      caution: ['175.80', 'revenue', 'count'],
    },
    criticalTerms: { risk_avoided: ['2'], caution: ['175.80'] },
  },
  {
    id: 'TD10',
    title: 'TD 10: The sign-off review',
    scenario: 'You are presenting the finished model to the Northstar data lead before the team starts learning SQL. She will not sign it off until you can state the grain of the original export in one sentence, name every table, and show that the model still answers the questions the business asks of the flat file.',
    tables: [{
      columns: ['Business question', 'Must still be answerable after the split'],
      rows: [
        ['Can one customer place many orders?', 'C-042 has O-1001 and O-1004'],
        ['Can one order contain many products?', 'O-1001 has P-100 and P-214'],
        ['Can one product appear in many orders?', 'P-100 is in O-1001, O-1003 and O-1007'],
        ['Can a store be renamed without editing sales rows?', 'S-03 Bristol West'],
        ['Can total sales still be calculated?', 'SUM(line_total) by order_id'],
      ],
    }],
    answerGuide: {
      business_question: 'Does the proposed model still answer every question the flat export answered, and what is the grain of that export in one sentence?',
      grain: 'The Northstar export is one row per order line: one product sold inside one order, which is why order_id repeats and order_line_id does not.',
      row_identifier: 'order_line_id is the row identifier of the export and becomes the primary key of OrderItems.',
      field_roles: 'Identifiers: order_id, order_line_id, customer_id, product_id, store_id. Date/time: order_date. Measures: quantity, unit_price, discount_pct, line_total. Dimensions: customer_name, customer_region, customer_segment, store_name, store_region, product_name, product_category, payment_method.',
      tables_proposed: 'Five tables — Customers (one row per customer), Products (one row per product), Stores (one row per store), Orders (one row per order), OrderItems (one row per order line).',
      keys: 'Customers PK customer_id; Products PK product_id; Stores PK store_id; Orders PK order_id with foreign keys customer_id and store_id; OrderItems PK order_line_id with foreign keys order_id and product_id.',
      relationship: 'Customers to Orders one-to-many; Stores to Orders one-to-many; Orders to OrderItems one-to-many; Products to OrderItems one-to-many; Orders to Products many-to-many resolved through OrderItems.',
      risk_avoided: 'Repeated facts and update anomalies — one customer, store or product detail can now be corrected in a single row instead of across every sales line, and order counts stop being inflated by line counts.',
      caution: 'The model answers the current questions, but it holds only the current version of each name and region. Anything needing "as it was at the time of sale" needs history on the order or a dated dimension.',
    },
    ruleTerms: {
      business_question: ['grain', 'answer', 'model'],
      grain: ['one row per order line', 'order line'],
      row_identifier: ['order_line_id', 'primary key'],
      field_roles: ['identifier', 'dimension', 'measure', 'date'],
      tables_proposed: ['customers', 'products', 'stores', 'orders', 'orderitems'],
      keys: ['customer_id', 'product_id', 'store_id', 'order_id', 'order_line_id'],
      relationship: ['one-to-many', 'many-to-many', 'orderitems'],
      risk_avoided: ['update anomal', 'repeated', 'single row'],
      caution: ['at the time', 'history', 'current'],
    },
    criticalTerms: { grain: ['order line'], tables_proposed: ['OrderItems'] },
  },
];

export const CT08_TABLE_DETECTIVE_CONFIG = {
  trackerId: CT08_TABLE_DETECTIVE_TRACKER_ID,
  resourceKey: 'ct_08_table_detective_exercise',
  rubricKey: 'data_tables_fields_relationships',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Table Detective Cases',
  description: 'Each case is a real request about the Northstar retail export. The answer is deliberately not named in the title — read the rows, work out the grain, then say where each fact belongs and why. Your work autosaves to your own practice record.',
  introAlert: 'Naming a table is never enough — state the grain first, then the keys, then the relationship, then the risk that splitting the flat table actually removes.',
  lenses: CT08_TABLE_DETECTIVE_LENSES,
  workingField: {
    key: 'model_sketch',
    label: 'Model sketch',
    placeholder: 'Example: OrderItems(order_line_id PK, order_id FK, product_id FK, quantity, unit_price, discount_pct, line_total) -> Orders(order_id PK, order_date, customer_id FK, store_id FK, payment_method).',
    prompts: CT08_TABLE_DETECTIVE_ITEMS.reduce((acc, item) => ({
      ...acc,
      [item.id]: `Sketch the tables this case needs for ${item.title}. Write each table as Name(primary key, foreign keys, fields) and draw the arrow between them.`,
    }), {}),
  },
  items: CT08_TABLE_DETECTIVE_ITEMS,
};
